import React from "react";
import { formatDistanceStrict } from "date-fns/esm";
import { format } from "date-fns";

import Title from "../../LoanTermDeviation/Title";
import Label from "../../../components/atomic/Label";
import Editor from "../../../components/atomic/Editor";
import Button from "../../../components/atomic/Button";
import { NotesType } from "../../../../unified-dashboard/src/pages/NotesTab/interface";

import { notify } from "../../../shared/context/AlertContext";
import { getValuesForNote } from "../../../../unified-dashboard/src/pages/NotesTab/utils";

interface NotesProps {
  notes: NotesType<boolean>[];
  onSubmit: any;
  close: () => void;
  isLenderPlatform: boolean;
}

export default React.memo(function Notes(props: NotesProps) {
  const { close, notes, onSubmit, isLenderPlatform } = props;

  const [values, setValue] = React.useState(
    {} as { value: string; length: number }
  );

  return (
    <div className="flex flex-col h-screen">
      <div className="px-10 py-3 border-b shadow">
        <Title
          title="Notes"
          close={close}
        />
      </div>

      <div className="flex-1 overflow-y-auto p-10">
        {notes?.map((note, index) => (
          <div
            key={index}
            className="mb-4 last:mb-0"
          >
            <NoteDesc
              note={note}
              isLenderPlatform={isLenderPlatform}
            />
            {index < notes.length - 1 && (
              <div className="border-t text-gray-600 mt-4"></div>
            )}
          </div>
        ))}
      </div>

      <div className="shadow-[0_-2px_12px_0px_rgba(0,0,0,0.05)] bg-white border-t p-5">
        <fieldset className="mb-4 ql-editor-style">
          <Label
            name="Add a note"
            label="Add a note"
          />

          <Editor
            key={values}
            initialValue={values?.value ?? ""}
            onChange={(value: any) => setValue(value)}
          />
        </fieldset>
        <div className="flex justify-end items-center">
          <Button
            color="primary"
            disabled={!Boolean(values?.value) || values?.length <= 1}
            onClick={() => {
              if (values?.length <= 1) {
                notify({
                  title: "Error",
                  text: "Please enter note"
                });
                return;
              }
              onSubmit({ note: values.value });
              setValue({});
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
});

function NoteDesc<T extends boolean>({
  note,
  isLenderPlatform
}: {
  note: NotesType<T>;
  isLenderPlatform: boolean;
}) {
  const { createdBy, userGroup, noteText, createdAt } = getValuesForNote(
    note,
    isLenderPlatform
  );

  return (
    <div className="flex flex-col justify-center">
      <div
        className={
          window.innerWidth < 500 ? "" : "flex items-center justify-between"
        }
      >
        <div className="flex">
          <p
            className="text-[12px]"
            id="created-by"
          >
            {createdBy}
          </p>
          {userGroup && (
            <>
              <span className="text-gray-600 text-xs mx-2">•</span>
              <p
                className="text-sm text-gray-600"
                id="user-group"
              >
                {userGroup}
              </p>
            </>
          )}
        </div>
        <p
          className="text-gray-600 text-[12px]"
          title={format(createdAt, "dd MMM, yyyy - hh:mm aa")}
          id="created-at"
        >
          {formatDistanceStrict(new Date(), createdAt)
            .replace("minute", "min")
            .replace("minutes", "mins")}{" "}
          ago
        </p>
      </div>

      <div className="notes-container mt-3">
        <div
          id="note-text"
          className="notes-content editor-style-container text-md font-normal"
          dangerouslySetInnerHTML={{ __html: noteText }}
        />
      </div>
    </div>
  );
}
